import mixPanel from 'mixpanel-browser';
import * as IoC from 'typescript-ioc';
import { v4 as uuidv4 } from 'uuid';
import { IUserDetailsDTO } from '../api/users.dao';
import { MIXPANEL_TRACKING_ENABLED } from '../constants/common.constants';
import { MIXPANEL_AVERAGE_SESSION_TIME } from '../constants/date-time.constants';
import { serviceAuthHeaderName } from '../infrastructure/api-client';
import { isProduction } from '../infrastructure/utils/common.utils';
import { convertBoolTypeForMixPanel } from './tracking-values';
import { AuthAggregateProvider } from '../domain/auth/implementations/auth-aggregate-provider';
import { IRuntimeConfiguration } from '../infrastructure/runtime-configuration';

interface IMixPanelSessionConfig {
    timeout: number;
    getSessionId: () => string;
    setSessionId: () => void;
    checkSessionId: () => void;
}

type IUserInfo = Omit<IUserDetailsDTO, 'username' | 'userPool'>;

class TrackingService {
    private mixPanelSessionConfig: IMixPanelSessionConfig;
    private authProvider: AuthAggregateProvider;
    constructor(
    ) {
        this.authProvider = IoC.Container.get(AuthAggregateProvider);
        this.mixPanelSessionConfig = {
            // thirty minutes in milliseconds
            timeout: MIXPANEL_AVERAGE_SESSION_TIME,

            getSessionId: (): string => {
                const uuid = uuidv4();
                return uuid;
            },
            // set a new session id
            setSessionId: function(): void {
                mixPanel.register({
                    'session ID': this.getSessionId(),
                });
            },

            // check for a new session id
            checkSessionId: (): void => {
                if (!mixPanel.get_property('last event time')) {
                    this.mixPanelSessionConfig.setSessionId();
                }

                if (!mixPanel.get_property('session ID')) {
                    this.mixPanelSessionConfig.setSessionId();
                }

                if (Date.now() - mixPanel.get_property('last event time') > this.mixPanelSessionConfig.timeout) {
                    this.mixPanelSessionConfig.setSessionId();
                }
            },
        };
    }

    public initialize = (mixpanelToken: string, sessionToken: string): void => {
        const runtimeConfiguration = IoC.Container.get(IRuntimeConfiguration);

        mixPanel.init(mixpanelToken, {
            property_blacklist: ['$browser_version'],
            loaded: () => {
                // mixpanel has loaded
                // check for a session_id ... if any of the checks fail set a new session id
                this.mixPanelSessionConfig.checkSessionId();

                // monkey patch to make sure that mixpanel.track() always checks for a session id
                // eslint-disable-next-line @typescript-eslint/unbound-method
                const originalTrack = mixPanel.track;
                mixPanel.track = (...args): void => {
                    this.mixPanelSessionConfig.checkSessionId();
                    mixPanel.register({ 'last event time': Date.now() });
                    originalTrack.apply(mixPanel, args);
                };
            },
            api_host: runtimeConfiguration.getApiRoot() + 'mixpanel',
            xhr_headers: {
                [serviceAuthHeaderName]: sessionToken,
            },
            batch_requests: false
        });
    };

    public identify = (username: string): void => {
        try {
            mixPanel.identify(username);
            // eslint-disable-next-line no-empty
        } catch (e) { }
    };

    public reset = (): void => {
        try {
            mixPanel.reset();
            // eslint-disable-next-line no-empty
        } catch (e) { }
    };

    public peopleSet = (currentUser: IUserInfo): void => {
        const {
            firstName,
            lastName,
            email,
            isOpenExchangeStaffMember,
            accounts,
            roles,
            isSuperuser,
            isAllAccounts,
        } = currentUser;
        const accountsList = accounts?.map(value => value.name).join();
        const rolesList = roles?.map(value => value.name).join();
        try {
            mixPanel.people.set({
                $email: email,
                $first_name: firstName,
                $last_name: lastName,
                Superuser: convertBoolTypeForMixPanel(isSuperuser),
                'Open Exchange Staff Member': convertBoolTypeForMixPanel(isOpenExchangeStaffMember),
                'Assigned accounts - All accounts': convertBoolTypeForMixPanel(isAllAccounts),
                'Assigned Accounts': accountsList,
                'Assigned Roles': rolesList,
            });
            // eslint-disable-next-line no-empty
        } catch (e) { }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public track = async (eventName: string, props: { [key: string]: any }): Promise<void> => {
        if (!isProduction() && localStorage.getItem(MIXPANEL_TRACKING_ENABLED) !== 'true') return;
        try {
            await this.getToken();
            mixPanel.track(eventName, props, { 'send_immediately': true });
            // eslint-disable-next-line no-empty
        } catch (e) { }
    };

    public setXHRHeader = (sessionToken: string): void => {
        try {
            mixPanel.set_config({
                xhr_headers: {
                    [serviceAuthHeaderName]: sessionToken,
                }
            });
            // eslint-disable-next-line no-empty
        } catch (e) { }
    };

    private getToken = async (): Promise<void> => {
        const sessionToken = await this.authProvider.get().getSessionToken();
        if (sessionToken) {
            this.setXHRHeader(sessionToken);
        }
    };
}

export default TrackingService;
